import MessageItemController from "controllers/layouts/message_threads/message_item_controller";
import http from "lib/http";
import BaseController from "confinus_ui/base_controller";
import parseDateTime from "lib/util/parse_datetime_attr";
import { MessageNotificationEvent } from "lib/types";
import { Layouts__MessageThreads__MessageItemControllerIdentifier } from "controllers/identifiers";
import DOMPurify from "dompurify";

export default class DynamicMessagesController extends BaseController {
  public static targets = ["messages"];

  public static values = {
    threadEncodedId: { type: String, default: null },
  };

  declare private readonly messagesTarget: HTMLElement;
  declare private readonly threadEncodedIdValue: any;

  public static outlets = [
    Layouts__MessageThreads__MessageItemControllerIdentifier,
  ];
  declare private readonly layoutsMessageThreadsMessageItemOutlets: MessageItemController[];
  private get messageControllers() {
    return this.layoutsMessageThreadsMessageItemOutlets;
  }

  public connect() {
    super.connect();
    // Fix missing scroll in Mozilla
    if ("InstallTrigger" in window) {
      this.messagesTarget.style.minHeight = "0";
    }

    this.scrollToBottom();

    this.removeAuthorsSeen();
  }

  public load(e: CustomEvent): void {
    const {
      detail: { message },
    } = e as MessageNotificationEvent;
    if (message.message_thread_encoded_id === this.threadEncodedIdValue) {
      this.fetchMessage(message.encoded_id, message.type, message.event_type);
    }
  }

  private fetchMessage(
    encoded_id: string,
    type: string,
    eventType: string,
  ): void {
    http
      .get<string>(`/account/messages/messages/${encoded_id}`)
      .then((response) => response.data)
      .then((html: string) =>
        this.addMessage(html, type, eventType, encoded_id),
      )
      .catch((e: Error) => {
        alert(e.message);
      });
  }

  private addMessage = (
    html: string,
    type: string,
    eventType: string,
    encoded_id: string,
  ) => {
    if (type === "EventMessage" && eventType === "messages_seen") {
      const previousSeen = this.messageControllers.find(
        (controller: MessageItemController) => {
          return encoded_id === controller.encoded_id;
        },
      );

      if (previousSeen) {
        this.removeItem(previousSeen);
      }
    }

    // Action Cable may not preserve order of new messages, restoring order
    const sentAt = parseDateTime(html);
    const beforeNode = this.firstItemSentAfter(sentAt);

    // Sanitize HTML to prevent XSS attacks before inserting into DOM
    const sanitizedHtml = DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        "div",
        "span",
        "p",
        "a",
        "img",
        "strong",
        "em",
        "br",
        "ul",
        "ol",
        "li",
        "time",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
      ],
      ALLOWED_ATTR: [
        "class",
        "href",
        "src",
        "alt",
        "title",
        "datetime",
        "data-controller",
        "data-action",
        "data-target",
        "data-layouts-message-threads-message-item-target",
        "data-layouts-message-threads-message-item-message-encoded-id-value",
        "data-layouts-message-threads-message-item-message-type-value",
        "data-layouts-message-threads-message-item-ago-value",
        "data-layouts-message-threads-message-item-sender-encoded-id-value",
        "data-base-timeago-target",
        "data-base-timeago-timestamp-value",
      ],
      ALLOW_DATA_ATTR: true,
      KEEP_CONTENT: true,
    });

    beforeNode
      ? beforeNode.scope.element.insertAdjacentHTML(
          "beforebegin",
          sanitizedHtml,
        )
      : this.messagesTarget.insertAdjacentHTML("beforeend", sanitizedHtml);

    this.scrollToBottom();
  };

  private scrollToBottom(): void {
    const listNode = this.element.children[0];
    listNode.scrollTop = listNode.scrollHeight;
  }

  // Time ago time entries:

  private firstItemSentAfter(
    sentAt: number,
  ): MessageItemController | undefined {
    return this.messageControllers.find(
      (controller: MessageItemController) => controller.sentAt > sentAt,
    );
  }

  // Removes notification that last message was seen about its author
  private removeAuthorsSeen(): void {
    const messageNodes = this.messageControllers.filter(
      (controller: MessageItemController) =>
        controller.messageType === "Message",
    );

    const lastMessageNode = messageNodes[messageNodes.length - 1];
    const technicalNodes = this.messageControllers.filter(
      (controller: MessageItemController) =>
        controller.messageType === "EventMessage",
    );

    if (!(lastMessageNode && technicalNodes)) {
      return;
    }

    const senderId = lastMessageNode.senderUid;

    for (const node of technicalNodes) {
      if (node.senderUid === senderId) {
        this.removeItem(node);
      }
    }
  }

  private removeItem(controller: MessageItemController) {
    const index = this.messageControllers.indexOf(controller);
    controller.scope.element.remove();
    this.messageControllers.splice(index, 1);
  }
}
